import React from 'react';
import { motion } from 'framer-motion';
import { Building2, ShoppingCart, Factory, Globe, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mock';
import { Link } from 'react-router-dom';
import BrandName from '../components/BrandName';

const iconMap = {
  'Enterprises': Building2,
  'Carbon Markets': Globe,
  'Manufacturing': Factory,
  'All Sectors': ShoppingCart,
};

const Solutions = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Solutions for Every Industry
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto">
              Tailored ESG intelligence and carbon management solutions that adapt to your specific industry needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {mockData.solutions.map((solution, index) => {
              const Icon = iconMap[solution.industry] || Building2;
              return (
                <motion.div
                  key={index}
                  className="card-premium p-12 rounded-3xl bg-white border-2 border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <div className="text-sm font-semibold mb-4" style={{ color: '#7CB8D6' }}>
                        {solution.industry}
                      </div>
                      <h3 className="text-3xl font-bold mb-4" style={{ color: '#1A1A1A' }}>
                        {solution.title}
                      </h3>
                    </div>
                    <div className="p-4 rounded-2xl" style={{ background: 'rgba(124, 184, 214, 0.1)' }}>
                      <Icon size={40} style={{ color: '#7CB8D6' }} />
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    {solution.description}
                  </p>

                  <div className="space-y-3">
                    {solution.features.map((feature, i) => (
                      <div key={i} className="flex items-center text-gray-700">
                        <CheckCircle size={20} className="mr-3 flex-shrink-0" style={{ color: '#F4D740' }} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-20"
            style={{ color: '#1A1A1A' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            What You Can Achieve
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12">
            <UseCaseCard
              title="Supply Chain Visibility"
              stat="Scope 3"
              statLabel="Emissions Mapped"
              description="Map emissions across your suppliers, pinpoint carbon hotspots, and prioritize the reductions that move the needle most."
            />
            <UseCaseCard
              title="Fraud-Free Carbon Credits"
              stat="100%"
              statLabel="Verifiable On-Chain"
              description="Blockchain verification helps eliminate double-counting and fraud, giving your stakeholders complete confidence in every credit."
            />
            <UseCaseCard
              title="Net-Zero Roadmapping"
              stat="AI-Guided"
              statLabel="Target Tracking"
              description="AI-powered forecasting projects your trajectory and flags risks early, so you can course-correct and stay on track to net-zero."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Find Your Solution
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Let's discuss how <BrandName /> can transform your sustainability strategy
            </p>
            <Link to="/contact">
              <motion.button
                className="btn-premium px-12 py-6 rounded-full text-xl font-semibold text-white"
                style={{ background: '#7CB8D6' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule Consultation
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const UseCaseCard = ({ title, stat, statLabel, description }) => {
  return (
    <motion.div
      className="p-10 rounded-3xl bg-white border-2 border-gray-100"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <h3 className="text-xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
        {title}
      </h3>
      <div className="mb-6">
        <div className="text-5xl font-bold mb-2" style={{ color: '#7CB8D6' }}>
          {stat}
        </div>
        <div className="text-sm font-semibold" style={{ color: '#F4D740' }}>
          {statLabel}
        </div>
      </div>
      <p className="text-gray-600 leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

export default Solutions;